import axios from "axios";
import { makeObservable, observable, computed } from "mobx";
import { jwtDecode } from "jwt-decode";

import { unregisterDevicePush } from "../native/push";

export class AppStore {
  private apiAddress: string;

  public accessToken: any = null;
  public clientRef: string = null;
  public events: any = [];
  public selectedEvent = null;
  public selectedClub: any = null;
  static instance: any;

  static getInstance() {
    this.instance = this.instance || new AppStore();
    return this.instance;
  }

  constructor() {
    makeObservable(this, {
      events: observable,
      selectedClub: observable,
      selectedEvent: observable,
      clientRef: observable,
      accessToken: observable,
      role: computed,
      isSuper: computed,
    });

    this.apiAddress = process.env.REACT_APP_API_ENDPOINT || "";
    this.clientRef = process.env.REACT_APP_CLIENT_REF || "";

    const sessionLocalStorage = localStorage.getItem("zw_events_session");
    if (sessionLocalStorage) {
      const session = JSON.parse(sessionLocalStorage);
      this.accessToken = session.accessToken;
      this.clientRef = session.clientRef;
    }
  }

  // Role always derives from the stored token; legacy tokens without the claim = admin.
  get role(): string {
    if (!this.accessToken) return "";
    try {
      return jwtDecode<{ role?: string }>(this.accessToken).role === "super" ? "super" : "admin";
    } catch {
      return "";
    }
  }

  get isSuper(): boolean {
    return this.role === "super";
  }

  loadEvents = async () => {
    this.events = await this.getData("events");
  };

  loadEvent = async (eventId: string) => {
    this.selectedEvent = this.events.find((event: any) => event._id === eventId);

    if (!this.selectedEvent) {
      this.selectedEvent = await this.getData(`events/${eventId}`);
    }
  };

  signOut = async () => {
    // Drop this device's push binding first — the DELETE needs the auth
    // header, and the hard redirect below would abort an in-flight call.
    await unregisterDevicePush(this).catch(() => {});

    localStorage.removeItem("zw_events_session");
    this.accessToken = "";
    this.clientRef = process.env.REACT_APP_CLIENT_REF || "";
    document.location.href = "/admin";
  };

  async getData(queryString: string) {
    const config: any = this.accessToken && {
      headers: {
        Authorization: "Bearer " + this.accessToken,
      },
    };

    const { data } = await axios.get(this.apiAddress + queryString, config).catch((error: any) => {
      console.error(error);
      throw error;
    });

    return data;
  }

  async postData(queryString: string, data: any) {
    // Attach the auth header when logged in (needed for admin coupon create). On the public
    // site accessToken is null, so no header is sent — identical to the previous behaviour, and
    // public routes ignore Authorization anyway.
    const config: any = this.accessToken
      ? { headers: { Authorization: "Bearer " + this.accessToken } }
      : undefined;

    const result = await axios.post(this.apiAddress + queryString, data, config).catch((error) => {
      console.error(error);
      return error.response;
    });

    return result;
  }

  validateCoupon = async ({ code, eventId, subEvent, email }: any) => {
    const result = await this.postData("coupons/validate", { code, eventId, subEvent, email });

    if (result && result.status >= 200 && result.status < 300 && result.data?.valid) {
      return { success: true, data: result.data };
    }

    return {
      success: false,
      error: result?.data?.message || "This coupon code is not valid.",
    };
  };

  async putData(queryString: string, payload: any) {
    const config: any = this.accessToken && {
      headers: {
        Authorization: "Bearer " + this.accessToken,
      },
    };

    try {
      const { data } = await axios.put(this.apiAddress + queryString, payload, config);
      return data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  async deleteData(queryString: string) {
    const config: any = this.accessToken && {
      headers: {
        Authorization: "Bearer " + this.accessToken,
      },
    };

    try {
      const { data } = await axios.delete(this.apiAddress + queryString, config);
      return data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }
}
